import { z } from 'zod'

/**
 * Prestacao de contas por evento (RF03.2).
 *
 * As categorias sao um plano de contas fechado, nao texto livre. Digitar
 * "alimentacao", "Alimentação" e "comida" em tres lancamentos diferentes
 * produziria tres linhas no balancete sintetico onde deveria haver uma - e um
 * balancete que nao agrupa nao serve para prestar contas a ninguem.
 *
 * O vocabulario abaixo e o mesmo que o Modulo 2 (Financeiro Geral) vai
 * consolidar, para a integracao futura nao exigir migracao de dados.
 */

export const entryTypeSchema = z.enum(['RECEITA', 'DESPESA'])
export type EntryType = z.infer<typeof entryTypeSchema>

export const REVENUE_CATEGORIES = [
  'VENDAS_BARRACA',
  'INSCRICOES',
  'PATROCINIO',
  'DOACOES',
  'RIFA',
  'LEILAO',
  'OUTRAS_RECEITAS',
] as const

export const EXPENSE_CATEGORIES = [
  'INSUMOS_ALIMENTOS',
  'INSUMOS_BEBIDAS',
  'ESTRUTURA_LOCACAO',
  'SOM_ILUMINACAO',
  'DIVULGACAO_GRAFICA',
  'PREMIACAO',
  'TAXAS_LICENCAS',
  'TRANSPORTE',
  'MANUTENCAO',
  'OUTRAS_DESPESAS',
] as const

export const ENTRY_CATEGORIES = [...REVENUE_CATEGORIES, ...EXPENSE_CATEGORIES] as const
export type EntryCategory = (typeof ENTRY_CATEGORIES)[number]

export const ENTRY_CATEGORY_LABELS: Record<EntryCategory, string> = {
  VENDAS_BARRACA: 'Vendas nas barracas',
  INSCRICOES: 'Inscricoes',
  PATROCINIO: 'Patrocinios',
  DOACOES: 'Doacoes',
  RIFA: 'Rifa',
  LEILAO: 'Leilao',
  OUTRAS_RECEITAS: 'Outras receitas',
  INSUMOS_ALIMENTOS: 'Insumos - alimentos',
  INSUMOS_BEBIDAS: 'Insumos - bebidas',
  ESTRUTURA_LOCACAO: 'Estrutura e locacao',
  SOM_ILUMINACAO: 'Som e iluminacao',
  DIVULGACAO_GRAFICA: 'Divulgacao e grafica',
  PREMIACAO: 'Premiacao',
  TAXAS_LICENCAS: 'Taxas e licencas',
  TRANSPORTE: 'Transporte',
  MANUTENCAO: 'Manutencao',
  OUTRAS_DESPESAS: 'Outras despesas',
}

export function categoriesForType(type: EntryType): readonly EntryCategory[] {
  return type === 'RECEITA' ? REVENUE_CATEGORIES : EXPENSE_CATEGORIES
}

export const entryCategorySchema = z.enum(ENTRY_CATEGORIES)

export const eventEntryInputSchema = z
  .object({
    type: entryTypeSchema,
    category: entryCategorySchema,
    description: z.string().min(3, 'Descreva o lancamento').max(200).trim(),
    /** Valor SEMPRE em centavos inteiros e SEMPRE positivo. */
    amountCents: z.coerce
      .number()
      .int('Informe um valor valido')
      .positive('O valor deve ser maior que zero'),
    occurredAt: z.iso.datetime({ offset: true }),
    receiptMediaId: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // O sinal do lancamento vem do `type`, nunca do valor. Permitir categoria
    // de despesa num lancamento de receita produziria um balancete que soma
    // errado sem dar nenhum aviso.
    const validas = categoriesForType(data.type)
    if (!validas.includes(data.category)) {
      ctx.addIssue({
        code: 'custom',
        path: ['category'],
        message:
          data.type === 'RECEITA'
            ? 'Esta categoria e de despesa. Selecione uma categoria de receita.'
            : 'Esta categoria e de receita. Selecione uma categoria de despesa.',
      })
    }
  })

export type EventEntryInput = z.infer<typeof eventEntryInputSchema>

export const eventEntryListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
  type: entryTypeSchema.optional(),
  category: entryCategorySchema.optional(),
  q: z.string().max(120).trim().optional(),
})

// --- Balancete -------------------------------------------------------------

/** Uma linha do balancete sintetico: total por categoria. */
export interface BalanceLine {
  category: EntryCategory
  categoryLabel: string
  totalCents: number
  count: number
}

/** Um lancamento do balancete analitico. */
export interface BalanceEntry {
  id: string
  type: EntryType
  category: EntryCategory
  categoryLabel: string
  description: string
  amountCents: number
  occurredAt: string
  hasReceipt: boolean
  createdByName: string | null
}

/**
 * Balancete do evento.
 *
 * `sintetico` agrupa por categoria (a visao que vai ao mural e a pagina
 * publica); `analitico` lista lancamento a lancamento (a visao que a tesouraria
 * confere). Os dois saem da mesma consulta para nunca discordarem.
 */
export interface EventBalance {
  event: { id: string; title: string; slug: string; startsAt: string }
  sintetico: {
    receitas: BalanceLine[]
    despesas: BalanceLine[]
    totalReceitasCents: number
    totalDespesasCents: number
    resultadoCents: number
  }
  analitico: BalanceEntry[]
  geradoEm: string
  /** Quantos lancamentos tem comprovante anexado - indicador de qualidade. */
  comComprovante: number
  totalLancamentos: number
}
